// @systeme: interface
// LE thème. Toute couleur, toute taille de référence vient d'ici. Aucun écran n'écrit
// une couleur en dur (une règle ESLint le vérifie).
//
// Intentions : chaleureux, calme, lisible d'une main dans le bruit. Pas de rouge
// accusateur : le rouge n'existe que pour confirmer une suppression.
import { createTheme } from '@mui/material/styles';

export const couleurs = {
  creme: '#FBF7F2', // le fond de l'app
  blanc: '#FFFFFF',
  sauge: '#5B6B4E', // la couleur principale, verte et posée
  saugeClair: '#E6ECE0',
  saugeFonce: '#46543C',
  terreCuite: '#C77B5A', // l'accent, pour LE geste principal de l'écran
  terreCuiteClair: '#F5E3D9',
  encre: '#2B2A27', // le texte
  gris: '#6E6A64', // le texte secondaire
  grisClair: '#E8E3DC', // les traits
  reussite: '#4E7A5C',
  attention: '#B5533C', // uniquement pour une action destructrice ou une vraie erreur
} as const;

export const tailles = {
  /** Hauteur minimale d'une cible tactile : une main, debout, dans le bruit. */
  cible: 48,
  navigation: 64,
  rayon: 14,
  /** Marge horizontale des écrans. */
  marge: 20,
} as const;

export const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: couleurs.sauge,
      light: couleurs.saugeClair,
      dark: couleurs.saugeFonce,
      contrastText: couleurs.blanc,
    },
    secondary: {
      main: couleurs.terreCuite,
      light: couleurs.terreCuiteClair,
      contrastText: couleurs.blanc,
    },
    background: { default: couleurs.creme, paper: couleurs.blanc },
    text: { primary: couleurs.encre, secondary: couleurs.gris },
    success: { main: couleurs.reussite },
    error: { main: couleurs.attention },
    divider: couleurs.grisClair,
  },
  shape: { borderRadius: tailles.rayon },
  typography: {
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    fontSize: 16,
    h1: { fontSize: '1.6rem', fontWeight: 700, lineHeight: 1.2 },
    h2: { fontSize: '1.2rem', fontWeight: 700, lineHeight: 1.3 },
    h3: { fontSize: '1.05rem', fontWeight: 600, lineHeight: 1.3 },
    body1: { fontSize: '1.05rem', lineHeight: 1.45 },
    body2: { fontSize: '0.95rem', lineHeight: 1.4 },
    button: { textTransform: 'none', fontWeight: 600, fontSize: '1.05rem' },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: { backgroundColor: couleurs.creme, overscrollBehaviorY: 'none' },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          minHeight: tailles.cible,
          borderRadius: tailles.rayon,
          paddingLeft: 20,
          paddingRight: 20,
        },
      },
    },
    MuiIconButton: {
      styleOverrides: { root: { minWidth: tailles.cible, minHeight: tailles.cible } },
    },
    MuiTextField: { defaultProps: { fullWidth: true, variant: 'outlined' } },
    MuiOutlinedInput: { styleOverrides: { root: { backgroundColor: couleurs.blanc } } },
    MuiBottomNavigation: {
      styleOverrides: { root: { height: tailles.navigation, backgroundColor: couleurs.blanc } },
    },
    MuiBottomNavigationAction: {
      styleOverrides: {
        root: {
          minWidth: 0,
          paddingTop: 8,
          color: couleurs.gris,
          '&.Mui-selected': { color: couleurs.sauge },
        },
        label: { fontSize: '0.75rem', '&.Mui-selected': { fontSize: '0.75rem' } },
      },
    },
    MuiPaper: { styleOverrides: { rounded: { borderRadius: tailles.rayon } } },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderTopLeftRadius: tailles.rayon * 1.5,
          borderTopRightRadius: tailles.rayon * 1.5,
        },
      },
    },
    MuiChip: { styleOverrides: { root: { height: 40, fontSize: '1rem', borderRadius: 20 } } },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          minHeight: tailles.cible,
          textTransform: 'none',
          fontSize: '1rem',
          '&.Mui-selected': { backgroundColor: couleurs.saugeClair, color: couleurs.saugeFonce },
        },
      },
    },
  },
});
